import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETS } from "../../../../utils/constants";
import { AssetInterface } from "../../../../models/AssetModel/Asset";
import assetService from "../../../../services/assetService/assetService";

interface UpdateAssetContext {
  previousAsset: AssetInterface[];
}

const useUpdateAsset = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<AssetInterface, Error, AssetInterface, UpdateAssetContext>(
    {
      mutationFn: async (asset: AssetInterface) => {
        // Assuming the ID is stored in the asset object or can be derived
        const { id, ...assetData } = asset;
        return assetService.Update(id || "", assetData);
      },

      onMutate: async (updatedAsset: AssetInterface) => {
        await queryClient.cancelQueries({
          queryKey: [CACHE_KEY_ASSETS, page, pageSize],
        });

        const previousAsset =
          queryClient.getQueryData<AssetInterface[]>([
            CACHE_KEY_ASSETS,
            page,
            pageSize,
          ]) || [];

        queryClient.setQueryData<AssetInterface[]>(
          [CACHE_KEY_ASSETS, page, pageSize],
          (assets = []) =>
            assets.map((asset) =>
              asset.id === updatedAsset.id ? updatedAsset : asset
            )
        );

        return { previousAsset };
      },

      onSuccess: (savedAsset) => {
        queryClient.setQueryData<AssetInterface[]>(
          [CACHE_KEY_ASSETS, page, pageSize],
          (assets) =>
            assets?.map((asset) =>
              asset.id === savedAsset.id ? savedAsset : asset
            )
        );
      },

      onError: (error, updatedAsset, context) => {
        if (!context) return;

        queryClient.setQueryData<AssetInterface[]>(
          [CACHE_KEY_ASSETS, page, pageSize],
          context.previousAsset
        );
      },
    }
  );
};

export default useUpdateAsset;
